import { useMemo, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { documentosService } from "@/services/documentos.service";
import { formatBytes, subcarpetaLabel } from "@/lib/documentos";
import type { DocumentoAmbito, DocumentosConfig } from "@/types";

interface UploadDocumentDialogProps {
  config: DocumentosConfig;
  ambito: DocumentoAmbito;
  /** Requerido cuando ambito === "licitacion". */
  licitacionId?: string | null;
  defaultSubcarpeta?: string;
  onUploaded: () => void;
  triggerLabel?: string;
  disabledReason?: string;
}

export function UploadDocumentDialog({
  config,
  ambito,
  licitacionId,
  defaultSubcarpeta,
  onUploaded,
  triggerLabel = "Subir documento",
  disabledReason,
}: UploadDocumentDialogProps) {
  const subcarpetas = useMemo(
    () => config.subcarpetas[ambito] ?? [],
    [config.subcarpetas, ambito],
  );
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [subcarpeta, setSubcarpeta] = useState(defaultSubcarpeta ?? subcarpetas[0] ?? "");
  const [tags, setTags] = useState("");
  const [vigencia, setVigencia] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const blocked = disabledReason ?? (ambito === "licitacion" && !licitacionId
    ? "Selecciona una licitación para adjuntar documentos"
    : !config.storageEnabled
      ? "Bucket no configurado: define GCS_BUCKET en el backend"
      : null);

  function validate(candidate: File | null): string | null {
    if (!candidate) return "Selecciona un archivo";
    if (candidate.size > config.maxFileBytes) {
      return `El archivo supera el máximo permitido (${formatBytes(config.maxFileBytes)})`;
    }
    if (config.allowedMime.length > 0 && !config.allowedMime.includes(candidate.type)) {
      return `Tipo de archivo no permitido: ${candidate.type || "desconocido"}`;
    }
    if (!subcarpeta) return "Selecciona una subcarpeta";
    return null;
  }

  async function handleSubmit() {
    const validation = validate(file);
    setError(validation);
    if (validation || !file) return;

    setSubmitting(true);
    try {
      const ticket = await documentosService.create({
        nombre: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        ambito,
        subcarpeta,
        licitacionId: ambito === "licitacion" ? licitacionId : null,
        tags: tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean)
          .slice(0, 10),
        vigenciaHasta: vigencia || null,
      });

      if (!ticket.upload) {
        toast.warning("Documento registrado, pero el bucket no está configurado");
      } else {
        await documentosService.upload(ticket.upload.url, file);
        await documentosService.confirm(ticket.documento.id, file.size);
        toast.success(`${file.name} subido correctamente`);
      }
      setOpen(false);
      setFile(null);
      setTags("");
      setVigencia("");
      onUploaded();
    } catch (err) {
      const message = err instanceof Error ? err.message : "No se pudo subir el documento";
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" disabled={Boolean(blocked)} title={blocked ?? undefined}>
          <Upload className="size-3.5" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Subir documento</DialogTitle>
          <DialogDescription>
            Se guardará en el bucket del tenant, dentro de la carpeta seleccionada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="doc-file">Archivo</Label>
            <Input
              id="doc-file"
              type="file"
              onChange={(event) => {
                const selected = event.target.files?.[0] ?? null;
                setFile(selected);
                setError(validate(selected));
              }}
              onBlur={() => setError(validate(file))}
            />
            {file ? (
              <p className="text-xs text-muted-foreground">
                {file.type || "tipo desconocido"} · {formatBytes(file.size)}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="doc-subcarpeta">Subcarpeta</Label>
            <Select value={subcarpeta} onValueChange={setSubcarpeta}>
              <SelectTrigger id="doc-subcarpeta">
                <SelectValue placeholder="Selecciona una subcarpeta" />
              </SelectTrigger>
              <SelectContent>
                {subcarpetas.map((sub) => (
                  <SelectItem key={sub} value={sub}>
                    {subcarpetaLabel(config, sub)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="doc-tags">Etiquetas</Label>
              <Input
                id="doc-tags"
                placeholder="legal, vigente"
                value={tags}
                onChange={(event) => setTags(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-vigencia">Vigente hasta</Label>
              <Input
                id="doc-vigencia"
                type="date"
                value={vigencia}
                onChange={(event) => setVigencia(event.target.value)}
              />
            </div>
          </div>

          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
            {submitting ? "Subiendo…" : "Subir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
